<!-- Dialog Title -->
<h2 mat-dialog-title class="m-0 hat-dialog-title">
    <div class="title-content">
        <mat-icon class="title-icon">{{isEditMode ? 'edit' : 'add_circle_outline'}}</mat-icon>
        <div class="title-text">
            <span>{{ dialogTitle }}</span>
            @if (dialogSubtitle) {
                <span class="title-subtitle">{{ dialogSubtitle }}</span>
            }
        </div>

        <!-- Hat Explorer button -->
        <button mat-icon-button
                class="explorer-button ml-2"
                (click)="openHatExplorer()"
                matTooltip="Open Hat Explorer - View hats by org post or assignment">
            <mat-icon>account_tree</mat-icon>
        </button>
    </div>
    <button mat-icon-button mat-dialog-close class="close-button" matTooltip="Close">
        <mat-icon>close</mat-icon>
    </button>
</h2>

<mat-dialog-content class="h-100-70 w-100">

    <mat-tab-group class="w-100 hat-tabs" [(selectedIndex)]="tabIndex" animationDuration="200ms">

        <!-- ═══════════════ TAB 1: CREATE / EDIT HAT ═══════════════ -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">{{ isEditMode ? 'edit' : 'add' }}</mat-icon>
                {{ isEditMode ? 'Edit Hat' : 'Create Hat' }}
            </ng-template>

            <div class="view-container m-3">
            <div class="hat-form-container">

                <!-- Processing overlay -->
                @if (isProcessing) {
                    <div class="processing-overlay">
                        <mat-progress-bar mode="indeterminate" color="accent"></mat-progress-bar>
                        <span class="processing-text">{{ isEditMode ? 'Updating hat...' : 'Creating hat...' }}</span>
                    </div>
                }

                <!-- Form Header -->
                <div class="form-header">
                    <div class="form-header-left">
                        <h3 class="form-title">{{ isEditMode ? 'Edit Hat Details' : 'Create a New Hat' }}</h3>
                        <p class="form-description">
                            {{ isEditMode ? 'Modify the hat configuration below.' : 'Define a role with permissions, staff, and optionally org posts. Hats without org posts apply org-wide.' }}
                        </p>
                    </div>
                    <div class="form-header-actions">
                        @if (isEditMode) {
                            <button mat-stroked-button (click)="resetForm()" matTooltip="Discard changes and start fresh">
                                <mat-icon>add</mat-icon>New Hat
                            </button>
                        }
                        <button mat-flat-button color="accent" (click)="saveHat()"
                            [disabled]="form.invalid || isProcessing || form.pristine">
                            <mat-icon>{{ isEditMode ? 'save' : 'check' }}</mat-icon>
                            {{ isEditMode ? 'Update' : 'Create' }}
                        </button>
                    </div>
                </div>

                <!-- Hat Form -->
                <form [formGroup]="form" class="hat-form">

                    <!-- Section: Basic Info -->
                    <div class="form-section">
                        <h4 class="section-title">
                            <mat-icon class="entity-hats">badge</mat-icon>
                            Basic Information
                        </h4>

                        <div class="form-row">
                            <mat-form-field appearance="outline" class="form-field-grow">
                                <mat-label>Hat Name</mat-label>
                                <input matInput type="text" formControlName="name" cdkFocusInitial
                                    placeholder="e.g. Financial Director" />
                                <mat-icon matPrefix>label</mat-icon>
                                @if (form.controls.name.hasError('required')) {
                                    <mat-error>Name is required</mat-error>
                                }
                                @if (form.controls.name.hasError('minlength')) {
                                    <mat-error>At least 3 characters</mat-error>
                                }
                            </mat-form-field>

                            <div class="toggle-container">
                                <mat-slide-toggle color="accent" formControlName="isMultiUsers">
                                    Multi-staff hat
                                </mat-slide-toggle>
                                <span class="toggle-hint">Allow multiple people to wear this hat</span>
                            </div>

                            <div class="toggle-container">
                                <mat-slide-toggle color="accent" formControlName="isSpecialRights">
                                    Special Rights hat
                                </mat-slide-toggle>
                                <span class="toggle-hint">Org-wide hat — assigned directly to users, never linked to a post</span>
                            </div>
                        </div>

                        <mat-form-field appearance="outline" class="w-100">
                            <mat-label>Description</mat-label>
                            <textarea formControlName="description" matInput
                                cdkTextareaAutosize
                                cdkAutosizeMinRows="2"
                                cdkAutosizeMaxRows="5"
                                placeholder="Describe what this hat represents..."
                            ></textarea>
                            <mat-icon matPrefix>description</mat-icon>
                        </mat-form-field>
                    </div>

                    <!-- Section: Assignments -->
                    <div class="form-section">
                        <h4 class="section-title">
                            <mat-icon class="entity-users">people</mat-icon>
                            Staff Assignment
                            @if (formStats.users > 0) {
                                <span class="section-badge badge-users">{{ formStats.users }}</span>
                            }
                        </h4>

                        <div class="inline-action-row">
                            <shared-searchable-select
                                class="flex-1-auto"
                                [displayValue]="false"
                                [appearance]="'outline'"
                                [classes]="['w-100']"
                                [label]="'Search and select staff'"
                                [data]="searchableUsers"
                                (selected)="onSelectUser($event)"
                                [control]="usersControl"
                                [multiple]="true"
                            ></shared-searchable-select>

                            <button mat-stroked-button color="accent" class="inline-add-btn"
                                (click)="openUserSelector()" matTooltip="Browse and select staff">
                                <mat-icon>flare</mat-icon>Browse
                            </button>
                        </div>

                        @if (getApprovedUserChips().length > 0) {
                            <div class="selected-chips">
                                @for (item of getApprovedUserChips(); track item.value) {
                                    <span class="selected-chip chip-user chip-clickable" (click)="viewUser(item.value)" matTooltip="Click to view staff member details">
                                        <mat-icon class="chip-icon">person</mat-icon>
                                        {{ item.display }}
                                        <mat-icon class="chip-remove" (click)="removeSelection(usersControl, item.value); $event.stopPropagation()">close</mat-icon>
                                    </span>
                                }
                            </div>
                        }

                        @if (getPendingUserChips().length > 0) {
                            <div class="pending-users-section">
                                <span class="pending-label">
                                    <mat-icon class="pending-label-icon">hourglass_empty</mat-icon>
                                    Pending Approval
                                </span>
                                <div class="selected-chips">
                                    @for (item of getPendingUserChips(); track item.value) {
                                        <span class="selected-chip chip-user chip-pending" matTooltip="Pending approval — remove to cancel">
                                            <mat-icon class="chip-icon">hourglass_empty</mat-icon>
                                            {{ item.display }}
                                            <mat-icon class="chip-remove" (click)="removeSelection(usersControl, item.value)">close</mat-icon>
                                        </span>
                                    }
                                </div>
                            </div>
                        }
                    </div>

                    <!-- Section: Security & Permissions -->
                    <div class="form-section">
                        <h4 class="section-title">
                            <mat-icon class="entity-security-groups">security</mat-icon>
                            Security Groups
                            @if (formStats.sgs > 0) {
                                <span class="section-badge badge-security-groups">{{ formStats.sgs }}</span>
                            }
                        </h4>

                        <div class="inline-action-row">
                            <shared-searchable-select
                                class="flex-1-auto"
                                [displayValue]="false"
                                [appearance]="'outline'"
                                [classes]="['w-100']"
                                [label]="'Search for security groups'"
                                [data]="searchableSecurityGroups"
                                [control]="securityGroupsControl"
                                (selected)="onSelectSG($event)"
                                [multiple]="true"
                            ></shared-searchable-select>

                            <button mat-stroked-button color="accent" class="inline-add-btn"
                                (click)="openSecurityGroupSelector()" matTooltip="Browse and select security groups">
                                <mat-icon>flare</mat-icon>Browse
                            </button>
                            <button mat-stroked-button color="accent" class="inline-add-btn"
                                (click)="newSecurityGroup()" matTooltip="Create a new security group">
                                <mat-icon>add</mat-icon>New
                            </button>
                        </div>

                        @if (getSelectedItems(securityGroupsControl, searchableSecurityGroups).length > 0) {
                            <div class="selected-chips">
                                @for (item of getSelectedItems(securityGroupsControl, searchableSecurityGroups); track item.value) {
                                    <span class="selected-chip chip-security chip-clickable" (click)="viewSecurityGroup(item.value)" matTooltip="Click to view details">
                                        <mat-icon class="chip-icon">security</mat-icon>
                                        {{ item.display }}
                                        <mat-icon class="chip-remove" (click)="removeSelection(securityGroupsControl, item.value); $event.stopPropagation()">close</mat-icon>
                                    </span>
                                }
                            </div>
                        }
                    </div>

                    <!-- Section: Positions (optional — leave empty for org-wide) -->
                    <div class="form-section">
                        <h4 class="section-title">
                            <mat-icon class="entity-positions">account_tree</mat-icon>
                            Org Posts <span class="optional-label">(optional)</span>
                            @if (formStats.positions > 0) {
                                <span class="section-badge badge-positions">{{ formStats.positions }}</span>
                            }
                        </h4>

                        <div class="inline-action-row">
                            <shared-searchable-select
                                class="flex-1-auto"
                                [displayValue]="false"
                                [appearance]="'outline'"
                                [classes]="['w-100']"
                                [label]="'Search for org posts'"
                                [data]="searchablePositions"
                                [control]="positionControl"
                                (selected)="onSelectPosition($event)"
                                [multiple]="true"
                                [icon]="'person'"
                                [iconColor]="'#1976d2'"
                            ></shared-searchable-select>

                            <button mat-stroked-button color="accent" class="inline-add-btn"
                                (click)="openPositionSelector()" matTooltip="Browse org posts in org tree">
                                <mat-icon>flare</mat-icon>Browse
                            </button>
                        </div>

                        @if (getSelectedItems(positionControl, searchablePositions).length === 0) {
                            <div class="position-hint">
                                <mat-icon class="hint-icon">info_outline</mat-icon>
                                <span>No org posts selected — this hat will apply <strong>org-wide</strong> to any assigned staff.</span>
                            </div>
                        }

                        @if (getSelectedItems(positionControl, searchablePositions).length > 0) {
                            <div class="selected-chips">
                                @for (item of getSelectedItems(positionControl, searchablePositions); track item.value) {
                                    <span class="selected-chip chip-position chip-clickable" (click)="viewPosition(item.value)" matTooltip="Click to view org post details">
                                        <mat-icon class="chip-icon">account_tree</mat-icon>
                                        {{ item.display }}
                                        <mat-icon class="chip-remove" (click)="removeSelection(positionControl, item.value); $event.stopPropagation()">close</mat-icon>
                                    </span>
                                }
                            </div>
                        }
                    </div>

                    <!-- Section: Policies -->
                    <div class="form-section">
                        <h4 class="section-title">
                            <mat-icon class="entity-policies">policy</mat-icon>
                            Policies
                            @if (formStats.policies > 0) {
                                <span class="section-badge badge-policies">{{ formStats.policies }}</span>
                            }
                        </h4>

                        <div class="inline-action-row">
                            <shared-searchable-select
                                class="flex-1-auto"
                                [displayValue]="false"
                                [appearance]="'outline'"
                                [classes]="['w-100']"
                                [label]="'Search and select policies'"
                                [data]="searchablePolicies"
                                (selected)="onSelectPolicy($event)"
                                [control]="policiesControl"
                                [multiple]="true"
                            ></shared-searchable-select>

                            <button mat-stroked-button color="accent" class="inline-add-btn"
                                (click)="openPolicySelector()" matTooltip="Browse and select policies">
                                <mat-icon>flare</mat-icon>Browse
                            </button>
                        </div>

                        @if (getSelectedItems(policiesControl, searchablePolicies).length > 0) {
                            <div class="selected-chips">
                                @for (item of getSelectedItems(policiesControl, searchablePolicies); track item.value) {
                                    <span class="selected-chip chip-policy chip-clickable" (click)="viewPolicy(item.value)" matTooltip="Click to view policy details">
                                        <mat-icon class="chip-icon">policy</mat-icon>
                                        {{ item.display }}
                                        <mat-icon class="chip-remove" (click)="removeSelection(policiesControl, item.value); $event.stopPropagation()">close</mat-icon>
                                    </span>
                                }
                            </div>
                        }
                    </div>

                </form>
            </div>
            </div>

        </mat-tab>


        <!-- ═══════════════ TAB 2: EXISTING HATS TABLE ═══════════════ -->
        <mat-tab>
            <ng-template mat-tab-label>
                <mat-icon class="tab-icon">list</mat-icon>
                Existing Hats
                @if (dataSource.data.length > 0) {
                    <span class="tab-badge">{{ dataSource.data.length }}</span>
                }
            </ng-template>

            <div class="view-container m-3 hats-view-flex">
            <div class="hats-list-container">

                <div class="toolbar-row">
                    <shared-org-search-bar
                        searchLabel="Search hats"
                        searchPlaceholder="Search by name, staff member, org post, group..."
                        [showPositionSearch]="true"
                        [positionNames]="allPositionNames"
                        [initialPositionTerm]="positionSearchTerm"
                        (searchChanged)="onSearchChanged($event)"
                        (positionSearchChanged)="onPositionSearchChanged($event)">
                    </shared-org-search-bar>

                    @if (orgDialogService.mayManageHats) {
                    <button mat-stroked-button color="warn" class="cleanup-button" (click)="openCleanupDialog()"
                            matTooltip="Open Hat Cleanup — find and bulk-delete unused hats or remove stale user assignments">
                        <mat-icon>cleaning_services</mat-icon>
                        Cleanup
                    </button>
                    }
                </div>

                <shared-org-filter-bar
                    [filters]="filterConfig"
                    [rightFilters]="rightFilterConfig"
                    [totalCount]="allHatsData.length"
                    activeColor="#1093ad"
                    activeDarkColor="#26c6da"
                    (filtersChanged)="onFiltersChanged($event)">
                </shared-org-filter-bar>

                <div class="table-scroll-container">
                @if (dataSource.data.length === 0) {
                    <div class="empty-state">
                        <mat-icon>sentiment_satisfied_alt</mat-icon>
                        <p>No hats created yet</p>
                        <span>Create your first hat in the form tab</span>
                    </div>
                } @else {
                    <mat-table [dataSource]="dataSource" class="hats-table">

                        <!-- Name column -->
                        <ng-container matColumnDef="name">
                            <mat-header-cell *matHeaderCellDef class="sortable-header" (click)="toggleSort('name')">
                                Name
                                @if (sortColumn === 'name') {
                                    <mat-icon class="sort-arrow">{{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                }
                            </mat-header-cell>
                            <mat-cell *matCellDef="let row">{{ row.name || '' }}</mat-cell>
                        </ng-container>

                        <!-- Description column -->
                        <ng-container matColumnDef="description">
                            <mat-header-cell *matHeaderCellDef>Description</mat-header-cell>
                            <mat-cell *matCellDef="let row" class="description-col">
                                {{ row.description ? (row.description.length > 50 ? row.description.substring(0, 50) + '...' : row.description) : '' }}
                            </mat-cell>
                        </ng-container>

                        <!-- Users column — stacked avatars -->
                        <ng-container matColumnDef="users">
                            <mat-header-cell *matHeaderCellDef class="users-col sortable-header" (click)="toggleSort('users')">
                                Staff
                                @if (sortColumn === 'users') {
                                    <mat-icon class="sort-arrow">{{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                }
                            </mat-header-cell>
                            <mat-cell *matCellDef="let row" class="users-col">
                                @if (getUsersForHat(row).length === 0 && getPendingUsersForHat(row).length === 0) {
                                    <span class="no-data">None</span>
                                } @else {
                                    <div class="stacked-avatars" [matTooltip]="getUserNamesTooltip(row)">
                                        @for (user of getUsersForHat(row) | slice:0:4; track user.key) {
                                            @if ((user.photoThumbnail || user.photoURL) && !brokenAvatars[user.key]) {
                                                <img class="avatar" [src]="user.photoThumbnail || user.photoURL" [alt]="user.name"
                                                     (error)="brokenAvatars[user.key] = true" />
                                            } @else {
                                                <span class="avatar avatar-initials" [style.background]="user.profileColor || '#9e9e9e'" style="color: #fff">{{ getInitials(user.name) }}</span>
                                            }
                                        }
                                        @if (getUsersForHat(row).length > 4) {
                                            <span class="avatar avatar-overflow">+{{ getUsersForHat(row).length - 4 }}</span>
                                        }
                                        @for (user of getPendingUsersForHat(row) | slice:0:3; track user.key) {
                                            <span class="pending-avatar-wrap">
                                                @if ((user.photoThumbnail || user.photoURL) && !brokenAvatars[user.key]) {
                                                    <img class="avatar avatar-pending" [src]="user.photoThumbnail || user.photoURL" [alt]="user.name"
                                                         (error)="brokenAvatars[user.key] = true" />
                                                } @else {
                                                    <span class="avatar avatar-initials avatar-pending" [style.background]="user.profileColor || '#9e9e9e'" style="color: #fff">{{ getInitials(user.name) }}</span>
                                                }
                                            </span>
                                        }
                                        @if (getPendingUsersForHat(row).length > 3) {
                                            <span class="pending-avatar-wrap">
                                                <span class="avatar avatar-overflow avatar-pending">+{{ getPendingUsersForHat(row).length - 3 }}</span>
                                            </span>
                                        }
                                    </div>
                                }
                            </mat-cell>
                        </ng-container>

                        <!-- Security Groups column — compact -->
                        <ng-container matColumnDef="securityGroups">
                            <mat-header-cell *matHeaderCellDef class="sg-col sortable-header" (click)="toggleSort('securityGroups')">
                                Security Groups
                                @if (sortColumn === 'securityGroups') {
                                    <mat-icon class="sort-arrow">{{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                }
                            </mat-header-cell>
                            <mat-cell *matCellDef="let row" class="sg-col">
                                @if (getSecurityGroupNames(row).length === 0) {
                                    <span class="no-data">None</span>
                                } @else {
                                    <span class="sg-count" [matTooltip]="getSecurityGroupNames(row).join(', ')">
                                        <mat-icon class="sg-icon">lock</mat-icon>
                                        {{ getSecurityGroupNames(row).length }}
                                    </span>
                                }
                            </mat-cell>
                        </ng-container>

                        <!-- Scope / Positions column -->
                        <ng-container matColumnDef="positions">
                            <mat-header-cell *matHeaderCellDef class="scope-col sortable-header" (click)="toggleSort('positions')">
                                Posts
                                @if (sortColumn === 'positions') {
                                    <mat-icon class="sort-arrow">{{ sortDirection === 'asc' ? 'arrow_upward' : 'arrow_downward' }}</mat-icon>
                                }
                            </mat-header-cell>
                            <mat-cell *matCellDef="let row" class="scope-col">
                                @if (row.isSpecialRights) {
                                    <span class="scope-chip scope-orgwide" matTooltip="This hat has the Special Rights flag — applies org-wide">
                                        <mat-icon class="scope-icon">public</mat-icon>
                                        Special Rights
                                    </span>
                                } @else if (isPositionless(row)) {
                                    <span class="scope-chip scope-no-position" matTooltip="This hat is not linked to any org post">
                                        <mat-icon class="scope-icon">block</mat-icon>
                                        No Org Post
                                    </span>
                                } @else {
                                    <span class="scope-chip scope-positioned"
                                          [matTooltip]="getPositionTooltip(row)"
                                          matTooltipClass="scope-tooltip-multiline">
                                        <mat-icon class="scope-icon">account_tree</mat-icon>
                                        @if (getPositionNames(row).length === 1) {
                                            {{ getPositionNames(row)[0] }}
                                        } @else {
                                            {{ getPositionNames(row)[0] }} (+{{ getPositionNames(row).length - 1 }})
                                        }
                                    </span>
                                }
                            </mat-cell>
                        </ng-container>

                        <ng-container matColumnDef="actions">
                            <mat-header-cell *matHeaderCellDef class="actions-col">Actions</mat-header-cell>
                            <mat-cell *matCellDef="let row" class="actions-col">
                                <button mat-icon-button color="primary" matTooltip="View details" (click)="viewHat(row)">
                                    <mat-icon>visibility</mat-icon>
                                </button>
                                <button mat-icon-button color="primary" matTooltip="Edit hat" (click)="editHat(row)">
                                    <mat-icon>edit</mat-icon>
                                </button>
                                <button mat-icon-button [matMenuTriggerFor]="menu" matTooltip="More actions">
                                    <mat-icon>more_vert</mat-icon>
                                </button>
                                <mat-menu #menu="matMenu">
                                    <button mat-menu-item (click)="viewHat(row)">
                                        <mat-icon>visibility</mat-icon>
                                        <span>View Details</span>
                                    </button>
                                    <button mat-menu-item (click)="editHat(row)">
                                        <mat-icon>edit</mat-icon>
                                        <span>Edit</span>
                                    </button>
                                    <button mat-menu-item class="delete-action" (click)="deleteHat(row)">
                                        <mat-icon color="warn">delete</mat-icon>
                                        <span>Delete</span>
                                    </button>
                                </mat-menu>
                            </mat-cell>
                        </ng-container>

                        <mat-header-row *matHeaderRowDef="displayedColumns; sticky: true"></mat-header-row>
                        <mat-row matRipple *matRowDef="let row; columns: displayedColumns;"
                            [class.orgwide-row]="row.isSpecialRights"
                            [class.post-row]="isHatLinkedToPost(row)"
                            (dblclick)="editHat(row)"></mat-row>
                    </mat-table>
                }
                </div>
            </div>
            </div>
        </mat-tab>

    </mat-tab-group>

</mat-dialog-content>

<mat-dialog-actions align="end">
    @if (isEditMode) {
        <button mat-button (click)="resetForm()">
            <mat-icon>add</mat-icon>New Hat
        </button>
    }
    <button mat-button mat-dialog-close>Close</button>
    @if (tabIndex === 0) {
        <button mat-flat-button color="accent" (click)="saveHat()"
            [disabled]="form.invalid || isProcessing || form.pristine">
            <mat-icon>{{ isEditMode ? 'save' : 'check' }}</mat-icon>
            {{ isEditMode ? 'Update Hat' : 'Create Hat' }}
        </button>
    }
</mat-dialog-actions>